import { type ClientWeatherCondition } from '@veriverse/shared';

/**
 * How the office weather looks in the game (spec §6.11 "In-game rendering"). Pure, so it can be
 * tested; `WeatherLayer` draws it.
 */
export interface WeatherEffects {
  readonly particles: 'none' | 'rain' | 'snow' | 'sleet';
  /** Drops per tile of outdoor or window area. */
  readonly density: number;
  /** 0 clear .. 1 overcast: greys the sky and dims outdoors a little. */
  readonly cloudiness: number;
  /** 0 .. 1 white haze over outdoor tiles and windows. */
  readonly fog: number;
  /** Occasional lightning flashes (never with reduced motion). */
  readonly lightning: boolean;
  /** Sideways drift of particles in pixels per second (from the wind). */
  readonly driftPxPerS: number;
}

export const NO_WEATHER: WeatherEffects = {
  particles: 'none',
  density: 0,
  cloudiness: 0,
  fog: 0,
  lightning: false,
  driftPxPerS: 0,
};

/** Most particles allowed with reduced motion ("no heavy particles", spec §14). */
const REDUCED_MOTION_DENSITY = 0.08;

const BASE: Record<
  ClientWeatherCondition,
  Pick<WeatherEffects, 'particles' | 'density' | 'cloudiness' | 'fog' | 'lightning'>
> = {
  unknown: { particles: 'none', density: 0, cloudiness: 0, fog: 0, lightning: false },
  clear: { particles: 'none', density: 0, cloudiness: 0, fog: 0, lightning: false },
  partly_cloudy: { particles: 'none', density: 0, cloudiness: 0.25, fog: 0, lightning: false },
  cloudy: { particles: 'none', density: 0, cloudiness: 0.55, fog: 0, lightning: false },
  fog: { particles: 'none', density: 0, cloudiness: 0.5, fog: 0.45, lightning: false },
  drizzle: { particles: 'rain', density: 0.12, cloudiness: 0.6, fog: 0, lightning: false },
  rain: { particles: 'rain', density: 0.3, cloudiness: 0.65, fog: 0, lightning: false },
  heavy_rain: { particles: 'rain', density: 0.6, cloudiness: 0.75, fog: 0.1, lightning: false },
  thunder: { particles: 'rain', density: 0.5, cloudiness: 0.8, fog: 0, lightning: true },
  sleet: { particles: 'sleet', density: 0.25, cloudiness: 0.65, fog: 0, lightning: false },
  snow: { particles: 'snow', density: 0.25, cloudiness: 0.55, fog: 0, lightning: false },
  heavy_snow: { particles: 'snow', density: 0.55, cloudiness: 0.7, fog: 0.15, lightning: false },
};

export function weatherEffects(
  condition: ClientWeatherCondition,
  precipitationMmH: number,
  windMs: number,
  reducedMotion: boolean,
): WeatherEffects {
  const base = BASE[condition];
  // More millimetres per hour, more drops (within reason).
  const amount = base.particles === 'none' ? 0 : clamp(0.7 + precipitationMmH / 4, 0.7, 1.5);
  const density = base.density * amount;
  return {
    ...base,
    density: reducedMotion ? Math.min(density, REDUCED_MOTION_DENSITY) : density,
    lightning: base.lightning && !reducedMotion,
    driftPxPerS: clamp(windMs, 0, 20) * (base.particles === 'snow' ? 4 : 6),
  };
}

/** One falling particle inside its area (pixels). */
export interface Drop {
  area: number;
  x: number;
  y: number;
  /** Fall speed factor (0.7..1.3) so drops do not move in lockstep. */
  speed: number;
}

export interface Area {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** Keeps a set of drops falling inside rectangles; they wrap around inside their own area. */
export class Precipitation {
  drops: Drop[] = [];

  constructor(
    readonly areas: readonly Area[],
    private readonly random: () => number = Math.random,
  ) {}

  /** Re-creates the drops for `perTile` drops per 32×32 tile, at most `max` in total. */
  setDensity(perTile: number, tileSize: number, max = 800): void {
    this.drops = [];
    if (perTile <= 0) return;
    this.areas.forEach((area, index) => {
      const tiles = (area.width * area.height) / (tileSize * tileSize);
      const count = Math.round(tiles * perTile);
      for (let i = 0; i < count && this.drops.length < max; i += 1) {
        this.drops.push({
          area: index,
          x: area.x + this.random() * area.width,
          y: area.y + this.random() * area.height,
          speed: 0.7 + this.random() * 0.6,
        });
      }
    });
  }

  /** Moves every drop down by `fallPxPerS` and sideways by `driftPxPerS`, wrapping in its area. */
  step(dtMs: number, fallPxPerS: number, driftPxPerS: number): void {
    const dt = dtMs / 1000;
    for (const drop of this.drops) {
      const area = this.areas[drop.area];
      if (!area) continue;
      drop.y += fallPxPerS * drop.speed * dt;
      drop.x += driftPxPerS * dt;
      drop.y = area.y + wrap(drop.y - area.y, area.height);
      drop.x = area.x + wrap(drop.x - area.x, area.width);
    }
  }
}

/** `value` modulo `size`, always in [0, size). */
function wrap(value: number, size: number): number {
  return ((value % size) + size) % size;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
